import { useEffect, useState } from "react";
import { Button } from "@trillion/ui/components/ui/button";
import { Textarea } from "@trillion/ui/components/ui/textarea";
import { Avatar, AvatarFallback } from "@trillion/ui/components/ui/avatar";
import { ScrollArea } from "@trillion/ui/components/ui/scroll-area";
import { Separator } from "@trillion/ui/components/ui/separator";
import { Badge } from "@trillion/ui/components/ui/badge";
import { cn } from "@trillion/ui/lib/utils";
import { toast } from "sonner";
import { api } from "../lib/api";
import type { DiagramComment } from "../types/board";

interface CommentsPanelProps {
  diagramId: string;
  open: boolean;
  onClose: () => void;
}

export function CommentsPanel({ diagramId, open, onClose }: CommentsPanelProps) {
  const [comments, setComments] = useState<DiagramComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [isReplying, setIsReplying] = useState(false);

  useEffect(() => {
    if (open) {
      loadComments();
    }
  }, [open, diagramId]);

  function loadComments() {
    setIsLoading(true);
    api<{ comments: DiagramComment[] }>(`/api/diagrams/${diagramId}/comments`)
      .then((res) => setComments(res.comments))
      .catch(() => toast.error("Failed to load comments"))
      .finally(() => setIsLoading(false));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newComment.trim()) return;
    setIsSubmitting(true);
    try {
      await api(`/api/diagrams/${diagramId}/comments`, {
        method: "POST",
        body: JSON.stringify({ content: newComment.trim() }),
      });
      setNewComment("");
      toast.success("Comment added");
      loadComments();
    } catch {
      toast.error("Failed to add comment");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleReply(parentId: string) {
    if (!replyContent.trim()) return;
    setIsReplying(true);
    try {
      await api(`/api/diagrams/${diagramId}/comments`, {
        method: "POST",
        body: JSON.stringify({
          content: replyContent.trim(),
          parent_comment_id: parentId,
        }),
      });
      setReplyContent("");
      setReplyingTo(null);
      toast.success("Reply added");
      loadComments();
    } catch {
      toast.error("Failed to add reply");
    } finally {
      setIsReplying(false);
    }
  }

  async function handleResolve(commentId: string) {
    try {
      await api(`/api/comments/${commentId}/resolve`, { method: "POST" });
      toast.success("Comment resolved");
      loadComments();
    } catch {
      toast.error("Failed to resolve comment");
    }
  }

  function getInitials(name?: string): string {
    return (name || "?")
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("");
  }

  function formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  // Separate top-level comments from replies
  const topLevel = comments.filter((c) => !c.parent_comment_id);
  const repliesByParent = comments.reduce<Record<string, DiagramComment[]>>((acc, c) => {
    if (c.parent_comment_id) {
      if (!acc[c.parent_comment_id]) acc[c.parent_comment_id] = [];
      acc[c.parent_comment_id].push(c);
    }
    return acc;
  }, {});

  if (!open) return null;

  return (
    <div className="board-side-panel flex h-full min-h-0 min-w-0 w-80 shrink-0 flex-col border-l bg-background">
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <h2 className="text-sm font-semibold">Comments</h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      <ScrollArea className="min-h-0 min-w-0 flex-1">
        <div className="space-y-1 p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading comments...</p>
          ) : topLevel.length === 0 ? (
            <p className="text-sm text-muted-foreground">No comments yet.</p>
          ) : (
            topLevel.map((comment) => {
              const replies = repliesByParent[comment.comment_id] ?? [];
              return (
                <div key={comment.comment_id} className="space-y-2">
                  <div
                    className={cn(
                      "rounded-lg border p-3",
                      comment.resolved && "opacity-60"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-[10px]">
                          {getInitials(comment.display_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">
                            {comment.display_name || "Unknown"}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {formatTime(comment.created_date)}
                          </span>
                        </div>
                        <p className="break-words text-sm">{comment.content}</p>
                        {comment.x !== null && comment.y !== null && (
                          <p className="text-[10px] text-muted-foreground">
                            Position: ({Math.round(comment.x)}, {Math.round(comment.y)})
                          </p>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          {comment.resolved ? (
                            <Badge variant="secondary" className="text-[10px]">
                              Resolved
                            </Badge>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => handleResolve(comment.comment_id)}
                            >
                              Resolve
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() =>
                              setReplyingTo(
                                replyingTo === comment.comment_id ? null : comment.comment_id
                              )
                            }
                          >
                            Reply
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {replies.map((reply) => (
                    <div
                      key={reply.comment_id}
                      className="ml-6 rounded-lg border p-3"
                    >
                      <div className="flex items-start gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-[9px]">
                            {getInitials(reply.display_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium">
                              {reply.display_name || "Unknown"}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {formatTime(reply.created_date)}
                            </span>
                          </div>
                          <p className="break-words text-sm">{reply.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {replyingTo === comment.comment_id && (
                    <div className="ml-6 space-y-2">
                      <Textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write a reply..."
                        rows={2}
                        className="text-sm"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          disabled={isReplying || !replyContent.trim()}
                          onClick={() => handleReply(comment.comment_id)}
                        >
                          {isReplying ? "Sending..." : "Reply"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyContent("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  <Separator className="my-2" />
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      <div className="border-t p-4">
        <form onSubmit={handleSubmit} className="space-y-2">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={2}
            className="text-sm"
          />
          <Button
            type="submit"
            size="sm"
            className="w-full"
            disabled={isSubmitting || !newComment.trim()}
          >
            {isSubmitting ? "Adding..." : "Add Comment"}
          </Button>
        </form>
      </div>
    </div>
  );
}
